import { useCallback, useEffect, useRef } from "react";

const throttle = (func, limit) => {
	let inThrottle;
	return function () {
		const args = arguments;
		const context = this;
		if (!inThrottle) {
			func.apply(context, args);
			inThrottle = true;
			setTimeout(() => (inThrottle = false), limit);
		}
	};
};

const onHoverGame = throttle((e) => {
	let position = [e.offsetX, e.offsetY];

	e.preventDefault();
	if (e.type === "touchmove") {
		position = [e.touches[0].clientX, e.touches[0].clientY];
	}
	const $card = e.currentTarget;
	const cardPosition = $card.getBoundingClientRect();

	const l = position[0];
	const t = position[1];
	const h = cardPosition.height;
	const w = cardPosition.width;
	const px = Math.abs(Math.floor((100 / w) * l) - 100);
	const py = Math.abs(Math.floor((100 / h) * t) - 100);

	const lp = 50 + (px - 50) / 1.5;
	const tp = 50 + (py - 50) / 1.5;

	$card.style.setProperty("--grad-pos", `${lp}% ${tp}%`);

	if (e.type === "touchmove") {
		return false;
	}
}, 16);

const onLeaveGame = (e) => {
	e.currentTarget.style.removeProperty("--grad-pos");
};

const styleCache = new Map();

const appendBaseAnimationStyle = (ref, forMostPlayed = false) => {
	const cacheKey = `${ref}-${forMostPlayed}`;

	if (styleCache.has(cacheKey)) {
		return;
	}

	const animationStyle = `${ref} {
		:root {
			--color1: var(--casino-game-hover-color);
			--color2: var(--casino-game-hover-inset);
		}
	backdrop-filter: ${window.CASINO_GAME_HOVER_BLUR ? " blur(2.5px)" : "none"};
	transition: all 0.5s ease-in-out;
    ${forMostPlayed ? "background: transparent;" : ""}
	&:before {
		content: "";
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-repeat: no-repeat;
		opacity: 0.5;
		mix-blend-mode: color-dodge;
		transition: all 0.33s ease;
		background-position: ${forMostPlayed ? "100% 100%" : "50% 50%"};
		background-size: 300% 300%;
		border-radius: 15px;
		background-image: linear-gradient(
			120deg,
			transparent 0%,
			var(--color1) 48%,
			transparent 47%,
			transparent 53%,
			var(--color2) 75%,
			transparent 100%
		);
		background: var(--casino-game-card-bg);
		opacity: 0.5;
		filter: brightness(0.5) contrast(1);
		z-index: 1;
	}
	&:hover:before {
		animation: none;
		background-image: linear-gradient(
			110deg,
			transparent 35%,
			var(--color1) 53%,
			var(--color2) 50%
			${!forMostPlayed ? ", var(--casino-game-card-bg) 65%" : ""}
		);
		background-position: var(--grad-pos);
		background-size: 250% 250%;
        ${forMostPlayed ? "background: var(--casino-game-card-bg);" : ""}
		opacity: 0.88;
		filter: brightness(0.86) contrast(1.33);
		transition: none;
	}
	}	
	`;

	const name = "mouse-animation-style" + ref.replaceAll("#", "");

	let styleTag = document.querySelector(`.${name}`);

	if (!styleTag) {
		let style = document.createElement("style");
		style.innerHTML = animationStyle;
		style.className = name;
		document.head.appendChild(style);
	}

	styleCache.set(cacheKey, true);
};

const useMousePositionAnimation = (ref, trigger, forMostPlayed = false) => {
	const elementsRef = useRef(new Set());
	const memoizedAppendBaseAnimationStyle = useCallback(() => {
		appendBaseAnimationStyle(ref, forMostPlayed);
	}, [ref, forMostPlayed]);

	useEffect(() => {
		memoizedAppendBaseAnimationStyle();

		const elements = document.querySelectorAll(ref);
		const currentElements = new Set(elements);

		elements.forEach((game) => {
			if (!elementsRef.current.has(game)) {
				game.addEventListener("mousemove", onHoverGame);
				game.addEventListener("mouseleave", onLeaveGame);
				game.addEventListener("touchmove", onHoverGame);
				game.addEventListener("touchend", onLeaveGame);
				elementsRef.current.add(game);
			}
		});

		elementsRef.current.forEach((game) => {
			if (!currentElements.has(game)) {
				game.removeEventListener("mousemove", onHoverGame);
				game.removeEventListener("mouseleave", onLeaveGame);
				game.removeEventListener("touchmove", onHoverGame);
				game.removeEventListener("touchend", onLeaveGame);
				elementsRef.current.delete(game);
			}
		});

		return () => {
			elementsRef.current.forEach((game) => {
				game.removeEventListener("mousemove", onHoverGame);
				game.removeEventListener("mouseleave", onLeaveGame);
				game.removeEventListener("touchmove", onHoverGame);
				game.removeEventListener("touchend", onLeaveGame);
			});
			elementsRef.current.clear();
		};
	}, [trigger, ref, memoizedAppendBaseAnimationStyle]);
};

export const useMousePositionAnimationForMostPlayed = (ref, trigger) =>
	useMousePositionAnimation(ref, trigger, true);

export { useMousePositionAnimation };
